import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Badge, Button, Checkbox } from '../ui';
import type { FeedbackFeed } from './useFeedbackFeed';
import { Banner, CopyButton, FeedbackNoteCard } from './FeedbackNoteCard';
import {
  canMerge, jobHasNoNotes, liveNotes, shotUrl, sourceWhere, STATUS_LABEL, STATUS_TONE, type FeedbackBatch, type FeedbackStatus,
} from '../../lib/clientFeedback';
import { timeAgo } from '../../lib/comms';
import Icon from '../ui/Icon';

/* Client feedback in the Comms feed: batches of notes clients pinned in
   feedback mode inside an app we built sit in Open / Assigned / Cleared next
   to email and Trello, typed "Feedback · <app>". Each note carries where it
   was pinned, the record, a screenshot and whatever failed quietly; each
   batch names the repo and databases its app lives in. Send a batch to the
   agent queue (one job, one release), or answer a question right here.
   Everything you do shows up in the client's app within a few minutes. The
   client can take a note back from their app: one nobody started just
   disappears, one already being worked on stays marked Withdrawn so whoever
   has it stops. Notes still New and out of the queue can be picked (a whole
   batch, or one note at a time) and merged into one group, only from one app
   (client_feedback_sources) at a time; the client's app regroups the same
   way, and the group goes to the queue as one. Pick boxes only show while
   "Select to merge" is on. */

const card: React.CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--border-hairline)',
  borderRadius: 'var(--radius-lg)',
};

/** The feed's type chip: "Feedback · Pete's Panels CRM". */
export function FeedbackTypeChip({ batch }: { batch: FeedbackBatch }) {
  return (
    <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
      <Badge tone="accent" dot>Feedback · {batch.source?.label ?? 'app'}</Badge>
      <Badge tone={STATUS_TONE[batch.status]}>{STATUS_LABEL[batch.status]}</Badge>
    </span>
  );
}

/** The first note's pinned crop, small, for the feed row. */
export function FeedbackThumb({ batch, size = 44 }: { batch: FeedbackBatch; size?: number }) {
  const first = batch.items.find((i) => i.crop_path) ?? null;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => { void shotUrl(first?.crop_path ?? null).then(setUrl); }, [first?.crop_path]);
  const box: React.CSSProperties = { width: Math.round(size * 1.5), height: size, flexShrink: 0, borderRadius: 6, border: '1px solid var(--border-hairline)' };
  return url
    ? <img src={url} alt="" style={{ ...box, objectFit: 'cover' }} />
    : <span style={{ ...box, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', background: 'var(--surface-raised)' }}><Icon name="message-square" size={14} /></span>;
}

/** Repo, branch and databases this app's work goes to. Compact for a feed row; in the panel, one line with copy buttons and the rest folded away. */
export function FeedbackWhere({ batch, compact = false }: { batch: FeedbackBatch; compact?: boolean }) {
  const [more, setMore] = useState(false);
  const where = sourceWhere(batch.source);
  if (!where.length) {
    return <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-warn)' }}>No repo or database recorded for {batch.source?.label ?? 'this app'}</span>;
  }
  if (compact) {
    return (
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }} title={where.map((w) => `${w.label}: ${w.value}`).join('\n')}>
        {where.filter((w) => w.label === 'Repo' || w.label === 'Prod DB').map((w) => w.value).join(' · ')}
      </span>
    );
  }
  const repo = batch.source?.repo ? `${batch.source.repo}${batch.source.repo_branch ? ` @ ${batch.source.repo_branch}` : ''}` : null;
  const db = batch.source?.db_ref ?? null;
  const rest = where.filter((w) => w.label !== 'Repo' && w.label !== 'Prod DB');
  const item = (icon: string, value: string, label: string, tag?: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
      <Icon name={icon} size={12} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>{value}</span>
      {tag ? <span style={{ fontSize: 'var(--text-2xs)', fontFamily: 'var(--font-mono)', letterSpacing: 'var(--tracking-label)', textTransform: 'uppercase', color: 'var(--text-faint)' }}>{tag}</span> : null}
      <CopyButton value={value} label={label} />
    </span>
  );
  return (
    <div style={{ fontSize: 'var(--text-xs)' }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px 12px' }}>
        {repo ? item('git-branch', repo, 'repo and branch') : null}
        {db ? item('database', db, 'prod database ref', 'prod') : null}
        {rest.length ? (
          <button
            type="button"
            className="ck-fb-disclosure hover-wash"
            aria-expanded={more}
            onClick={() => setMore((m) => !m)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', border: 'none', borderRadius: 'var(--radius-xs)',
              background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 'var(--text-xs)', color: 'var(--text-faint)',
            }}
          >
            {more ? 'Less' : `${rest.map((w) => w.label.replace(' DB', ' DB')).join(', ')}`}
            <Icon name="chevron-down" size={12} className="ck-fb-chevron-down" />
          </button>
        ) : null}
      </div>
      {more ? (
        <div className="ck-fb-reveal" style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', alignItems: 'center', gap: '0 12px', marginTop: 6, padding: '6px 10px', background: 'var(--surface-raised)', borderRadius: 'var(--radius-sm)' }}>
          {rest.map((w) => (
            <span key={w.label} style={{ display: 'contents' }}>
              <span style={{ color: 'var(--text-faint)' }}>{w.label}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-body)', overflowWrap: 'anywhere' }}>{w.value}</span>
                <CopyButton value={w.value} label={w.label} />
              </span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** "Select to merge" next to the feed tabs: shows the pick boxes on feedback that can still be merged. */
export function FeedbackSelectToggle({ feed }: { feed: FeedbackFeed }) {
  const mergeable = feed.batches.filter(canMerge).length;
  if (mergeable < 2 || feed.selecting) return null;
  return (
    <Button size="sm" variant="ghost" icon="layers" onClick={() => feed.setSelecting(true)} title="Pick notes from two or more groups of one app and merge them">
      Select to merge
    </Button>
  );
}

/** The sticky bar while "Select to merge" is on: what's picked, then Merge into one group. */
export function FeedbackMergeBar({ feed }: { feed: FeedbackFeed }) {
  const { pick, mergeable, mergeReason, confirmMerge, setConfirmMerge, merging, merge, setSelecting, me, selecting } = feed;
  if (!selecting) return null;
  const n = pick.ids.length;
  return (
    <div
      className="ck-menu-pop"
      role="toolbar"
      aria-label="Merge picked notes"
      style={{
        ...card,
        position: 'sticky', top: 8, zIndex: 5, marginBottom: 'var(--space-4)', padding: '10px 14px',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px',
        transformOrigin: 'top center',
        boxShadow: '0 10px 30px -14px rgba(0,0,0,0.45)',
        borderColor: 'color-mix(in srgb, var(--accent-text) 35%, var(--border-hairline))',
      }}
    >
      <Icon name="layers" size={15} style={{ color: 'var(--accent-text)' }} />
      <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>
        {n ? `${n} note${n === 1 ? '' : 's'} picked from ${pick.from.length} group${pick.from.length === 1 ? '' : 's'}` : 'Select to merge'}
      </span>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)', flex: '1 1 200px', minWidth: 0 }}>
        {!n
          ? 'Pick notes from two or more groups of one app. Only New notes outside the agent queue.'
          : mergeReason
            ?? (confirmMerge
              ? `They join the group from ${pick.oldest ? timeAgo(pick.oldest.sent_at ?? pick.oldest.received_at) : 'earliest'}; emptied groups go. The client’s app regroups too.`
              : `${pick.sourceLabel ?? 'Same app'} · New, not queued`)}
      </span>
      <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        {confirmMerge ? (
          <>
            <Button size="sm" variant="ghost" disabled={merging} onClick={() => setConfirmMerge(false)}>Keep apart</Button>
            <Button size="sm" icon="layers" disabled={merging || !mergeable} onClick={() => void merge()}>{merging ? 'Merging…' : 'Merge'}</Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="ghost" onClick={() => setSelecting(false)}>{n ? 'Cancel' : 'Done'}</Button>
            {n ? (
              <span title={mergeReason ?? undefined}>
                <Button size="sm" icon="layers" disabled={!mergeable || !me} onClick={() => setConfirmMerge(true)}>Merge into one group</Button>
              </span>
            ) : null}
          </>
        )}
      </span>
    </div>
  );
}
/** Pick a whole batch for a merge, from the feed row, while "Select to merge" is on. Disabled with the reason when it can't join the pick. */
export function FeedbackPickBox({ feed, batch }: { feed: FeedbackFeed; batch: FeedbackBatch }) {
  if (!feed.selecting || !canMerge(batch)) return null;
  const block = feed.pickBlock(batch);
  const all = batch.items.length > 0 && batch.items.every((i) => feed.picked.has(i.id));
  return (
    <span title={block ?? 'Pick this whole group to merge'} onClick={(e) => e.stopPropagation()} className="ck-fb-reveal" style={{ display: 'inline-flex' }}>
      <Checkbox checked={all} disabled={!!block} onChange={(on) => feed.toggle(batch.items.map((i) => i.id), on)} />
    </span>
  );
}

/** The submission note Bullfinch posted on this group (job page → Submit Note), as the client sees it. */
export function FeedbackSubmissionNote({ batch }: { batch: FeedbackBatch }) {
  if (!batch.submission_note) return null;
  return (
    <section aria-label="Submission note" className="ck-menu-pop" style={{
      padding: '12px 14px', border: '1px solid color-mix(in srgb, var(--status-up) 28%, transparent)', background: 'var(--status-up-soft)',
      borderRadius: 'var(--radius-md)', transformOrigin: 'top left',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        <Icon name="check-circle-2" size={13} style={{ color: 'var(--status-up)' }} />
        <span style={{ fontWeight: 600, color: 'var(--status-up)' }}>Submission note · posted to the client</span>
        <span>{[batch.submission_note_at ? timeAgo(batch.submission_note_at) : null, batch.submission_note_by_name ? `by ${batch.submission_note_by_name}` : null].filter(Boolean).join(' ')}</span>
      </div>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--text-sm)', lineHeight: 1.55, color: 'var(--text-body)' }}>{batch.submission_note}</div>
    </section>
  );
}

/** "2 New · 1 Withdrawn": how many notes sit in each status, in the order work moves. */
function StatusCounts({ batch }: { batch: FeedbackBatch }) {
  const order: FeedbackStatus[] = ['received', 'in_progress', 'preview', 'live', 'answered', 'closed', 'withdrawn'];
  const counts = order.map((s) => [s, batch.items.filter((i) => i.status === s).length] as const).filter(([, n]) => n > 0);
  return (
    <>
      {counts.map(([s, n]) => <Badge key={s} tone={STATUS_TONE[s]}>{n} {STATUS_LABEL[s] === 'Withdrawn by client' ? 'Withdrawn' : STATUS_LABEL[s]}</Badge>)}
    </>
  );
}

/** The side panel body for one batch: who sent it and where it goes, flags, the client's note, every note as a card. */
export function FeedbackBatchDetail({ feed, batch }: { feed: FeedbackFeed; batch: FeedbackBatch }) {
  const b = batch;
  const live = liveNotes(b).length;
  const gone = b.items.length - live;
  const block = feed.pickBlock(b);
  const sender = b.sender?.full_name ?? 'The client';
  const first = sender.split(' ')[0];
  const mergeable = canMerge(b);
  const selecting = feed.selecting && mergeable;
  const allPicked = b.items.length > 0 && b.items.every((i) => feed.picked.has(i.id));
  const at = b.sent_at ?? b.received_at;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {/* Who, when, how many, and where the work goes */}
      <header style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Avatar name={sender} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--text-base)', color: 'var(--text-body)' }}>
              <strong style={{ fontWeight: 600 }}>{sender}</strong>
              <span style={{ color: 'var(--text-muted)' }}> sent {b.items.length} note{b.items.length === 1 ? '' : 's'} from {b.source?.label ?? 'their app'}</span>
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: 2 }} title={new Date(at).toLocaleString()}>
              {timeAgo(at)} · {new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              {b.sender?.email ? ` · ${b.sender.email}` : ''}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              <StatusCounts batch={b} />
              {b.ai_job_id ? <Badge tone="info" title="This group is in the agent queue">Agent queue{b.job?.status ? ` · ${b.job.status.replace(/_/g, ' ')}` : ''}</Badge> : null}
            </div>
          </div>
          {mergeable && !feed.selecting ? (
            <Button size="sm" variant="ghost" icon="layers" onClick={() => feed.setSelecting(true)}>Select to merge</Button>
          ) : null}
        </div>
        <div style={{ paddingTop: 10, borderTop: '1px solid var(--border-hairline)' }}>
          <FeedbackWhere batch={b} />
        </div>
      </header>

      {jobHasNoNotes(b) ? (
        <Banner
          tone="down"
          title="Every note was withdrawn, but the job is still open"
          action={<Link to={`/agents?job=${b.ai_job_id}`}><Button size="sm" variant="secondary" icon="external-link">Open job</Button></Link>}
        >
          Stop work on it, and cancel the job if nothing else needs it.
        </Banner>
      ) : gone > 0 && (b.ai_job_id || b.status !== 'received') ? (
        <Banner tone="warn" title={`${first} withdrew ${gone} of ${b.items.length} notes${b.withdrawn_at ? ` ${timeAgo(b.withdrawn_at)}` : ''}`}>
          Leave {gone === 1 ? 'it' : 'them'} out of the work.
        </Banner>
      ) : null}

      {b.note ? (
        <figure style={{
          margin: 0, padding: '12px 16px', background: 'var(--surface-raised)', borderLeft: '3px solid var(--accent)',
          borderRadius: '0 var(--radius-md) var(--radius-md) 0',
        }}>
          <figcaption style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            <Icon name="message-square" size={12} /> {first}’s note on this batch
          </figcaption>
          <blockquote style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 'var(--text-md)', lineHeight: 1.5, color: 'var(--text-body)' }}>{b.note}</blockquote>
        </figure>
      ) : null}

      <FeedbackSubmissionNote batch={b} />

      <section aria-label="Notes" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {selecting ? (
          block ? (
            <Banner tone="info" icon="lock">{block}</Banner>
          ) : (
            <div className="ck-fb-reveal" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              <Checkbox checked={allPicked} onChange={(on) => feed.toggle(b.items.map((i) => i.id), on)} label={`Pick all ${b.items.length} notes`} />
            </div>
          )
        ) : null}
        {b.items.map((it, i) => (
          <FeedbackNoteCard
            key={it.id}
            item={it}
            index={i}
            me={feed.me}
            sender={first}
            onChanged={feed.load}
            pick={selecting ? { checked: feed.picked.has(it.id), disabled: !!block, reason: block, onChange: (on) => feed.toggle([it.id], on) } : undefined}
          />
        ))}
      </section>
    </div>
  );
}

/** Footer for a batch's panel: Clear / Reopen on the left, Send to agent queue (or Open job) on the right. */
export function FeedbackBatchActions({ feed, batch }: { feed: FeedbackFeed; batch: FeedbackBatch }) {
  const live = liveNotes(batch).length;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
      <Button size="sm" variant="secondary" icon={batch.cleared_at ? 'rotate-ccw' : 'check'} onClick={() => void feed.clear(batch, !batch.cleared_at)}>
        {batch.cleared_at ? 'Reopen' : 'Clear'}
      </Button>
      <span style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', textAlign: 'right', minWidth: 0 }}>
        {batch.ai_job_id
          ? null
          : live
            ? `${live} note${live === 1 ? '' : 's'} go${live === 1 ? 'es' : ''} as one job`
            : 'Every note was withdrawn'}
      </span>
      {batch.ai_job_id ? (
        <Link to={`/agents?job=${batch.ai_job_id}`}><Button size="sm" icon="external-link">Open job</Button></Link>
      ) : (
        <Button size="sm" icon="sparkles" disabled={feed.busy === batch.id || !feed.me || !live} onClick={() => void feed.send(batch)}>
          {feed.busy === batch.id ? 'Sending…' : 'Send to agent queue'}
        </Button>
      )}
    </span>
  );
}
